import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Email,
  Phone,
  Edit,
  Diamond
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import { customerAPI } from '../services/api';
import BottomNav from '../components/BottomNavigation';
import ProfileGridMenu from '../components/ProfileGridMenu';
import useResponsive from '../hooks/useResponsive';
import { formatCurrency } from '../utils/countryUtils';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [statistics, setStatistics] = useState({
    totalBookings: 0,
    totalOrders: 0,
    memberSince: 'Jan 2024'
  });
  const [walletBalance, setWalletBalance] = useState(0);
  const [gemsBalance, setGemsBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Debug logging
  console.log('👤 Profile component - user data:', user);
  console.log('👤 Profile component - profilePhotoUrl:', user?.profilePhotoUrl);
  console.log('👤 Profile component - phone fields:', {
    phone: user?.phone,
    phoneNumber: user?.phoneNumber,
    mobile: user?.mobile,
    mobileNumber: user?.mobileNumber
  });

  // Fetch user statistics and wallet data
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const response = await customerAPI.getStatistics();
        if (response.success) {
          setStatistics({
            totalBookings: response.data.totalBookings || 0,
            totalOrders: response.data.totalOrders || 0,
            memberSince: response.data.memberSince || 'Jan 2024'
          });
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    const fetchWalletData = async () => {
      try {
        const walletAPI = (await import('../services/api')).walletAPI;
        const response = await walletAPI.getWalletBalance();
        if (response.success) {
          setWalletBalance(response.data.balance || 0);
          setGemsBalance(response.data.gems || 0);
        }
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        // Keep default values on error
      }
    };

    if (user) {
      fetchStatistics();
      fetchWalletData();
    }
  }, [user]);

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  return (
    <Box sx={{ pb: 8 }} key={user?.profilePhotoUrl}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#0A2A66', 
        color: 'white', 
        p: { xs: 2, md: 3 },
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Profile
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Manage your account and preferences
        </Typography>
      </Box>

      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2 
        }}>
        {/* Profile Card - Blue Background like Provider App */}
        <Card sx={{ 
          mb: 3,
          background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
          color: 'white'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1.5, md: 3 }, 
              mb: { xs: 2, md: 3 } 
            }}>
              <Avatar 
                src={user?.profilePhotoUrl || user?.avatar}
                sx={{ 
                  width: { xs: 50, md: 80 }, 
                  height: { xs: 50, md: 80 }, 
                  backgroundColor: '#ffffff',
                  color: '#0A2A66',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  border: '3px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  mb: 0.5,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  color: 'white',
                  wordBreak: 'break-word'
                }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="body2" sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  color: 'rgba(255, 255, 255, 0.9)',
                  wordBreak: 'break-word'
                }}>
                  {user?.email}
                </Typography>
                <Chip 
                  label="Verified" 
                  size="small" 
                  sx={{ 
                    fontSize: '0.7rem',
                    height: 22,
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<Edit sx={{ fontSize: { xs: 14, md: 18 } }} />}
                onClick={() => navigate('/edit-profile')}
                sx={{
                  background: '#4CAF50',
                  color: 'white',
                  borderRadius: 2,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.75, sm: 1 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: { xs: '70px', sm: 'auto' },
                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    background: '#45a049',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Edit
              </Button>
            </Box>

                         <Divider sx={{ my: { xs: 1.5, md: 2 }, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

             {/* Contact Info and Statistics */}
             <Box sx={{ 
               display: 'grid', 
               gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
               gap: { xs: 2, md: 2 } 
             }}>
               {/* Contact Information */}
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, md: 1 } }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <Email sx={{ fontSize: { xs: 16, md: 20 }, color: '#4CAF50' }} />
                   <Typography variant="body2" sx={{ 
                     wordBreak: 'break-word',
                     fontSize: { xs: '0.75rem', md: '0.875rem' },
                     color: 'rgba(255, 255, 255, 0.95)',
                     lineHeight: 1.4
                   }}>
                     {user?.email || 'No email provided'}
                   </Typography>
                 </Box>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <Phone sx={{ fontSize: { xs: 16, md: 20 }, color: '#2196F3' }} />
                   <Typography variant="body2" sx={{ 
                     fontSize: { xs: '0.75rem', md: '0.875rem' },
                     color: 'rgba(255, 255, 255, 0.95)'
                   }}>
                     {user?.phone || user?.phoneNumber || user?.mobile || user?.mobileNumber || 'No phone provided'}
                   </Typography>
                 </Box>
                 
                 {/* Wallet & Gems Display - Compact on Mobile */}
                 <Box sx={{ 
                   display: 'flex', 
                   gap: { xs: 1, md: 1.5 }, 
                   alignItems: 'center', 
                   flexWrap: 'wrap', 
                   mt: { xs: 0.5, md: 1 },
                   p: { xs: 1, md: 0 },
                   backgroundColor: { xs: 'rgba(255, 255, 255, 0.15)', md: 'transparent' },
                   borderRadius: { xs: 1.5, md: 0 }
                 }}>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <Typography variant="body2" sx={{ 
                       color: '#4CAF50', 
                       fontWeight: 'bold',
                       fontSize: { xs: '0.85rem', md: '0.875rem' }
                     }}>
                       {formatCurrency(walletBalance)}
                     </Typography>
                   </Box>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <Diamond sx={{ fontSize: { xs: 18, md: 20 }, color: '#FFD700' }} />
                     <Typography variant="body2" sx={{ 
                       color: '#FFD700', 
                       fontWeight: 'bold',
                       fontSize: { xs: '0.85rem', md: '0.875rem' }
                     }}>
                       {gemsBalance} Gems
                     </Typography>
                   </Box>
                 </Box>
               </Box>

                               {/* Account Statistics */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { 
                    xs: 'repeat(3, 1fr)', 
                    md: 'repeat(3, 1fr)' 
                  }, 
                  gap: { xs: 1.5, md: 2 }
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: { xs: '0.65rem', md: '0.75rem' },
                      lineHeight: 1.2,
                      mb: 0.5,
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.8)'
                    }}>
                      Total Bookings
                    </Typography>
                    {loading ? (
                      <CircularProgress size={16} sx={{ color: '#4CAF50' }} />
                    ) : (
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#4CAF50',
                        fontSize: { xs: '0.95rem', md: '1.25rem' }
                      }}>
                        {statistics.totalBookings}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: { xs: '0.65rem', md: '0.75rem' },
                      lineHeight: 1.2,
                      mb: 0.5,
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.8)'
                    }}>
                      Product Orders
                    </Typography>
                    {loading ? (
                      <CircularProgress size={16} sx={{ color: '#4CAF50' }} />
                    ) : (
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#4CAF50',
                        fontSize: { xs: '0.95rem', md: '1.25rem' }
                      }}>
                        {statistics.totalOrders}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: { xs: '0.65rem', md: '0.75rem' },
                      lineHeight: 1.2,
                      mb: 0.5,
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.8)'
                    }}>
                      Member Since
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      color: '#FFD700',
                      fontSize: { xs: '0.85rem', md: '1.25rem' },
                      wordBreak: 'break-word'
                    }}>
                      {statistics.memberSince}
                    </Typography>
                  </Box>
                </Box>
             </Box>
          </CardContent>
        </Card>

        {/* Profile Grid Menu */}
        <ProfileGridMenu />
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default Profile; 